import { useCallback, useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const POLL_INTERVAL_MS = 30000;
const CACHE_KEY = 'pcg_impersonation_state_v1';

/**
 * useImpersonation
 *
 * Single source of truth for whether the current session is acting on behalf
 * of another organization. The platform_superuser can start an impersonation
 * session from /platform-admin → "View as". While that session is active, the
 * UI surfaces a persistent red banner and disables write controls.
 *
 * The hook returns:
 *   { active: false, loading }
 *   { active: true, target_org, expires_at, started_at, reason, mode,
 *     minutesRemaining, endImpersonation, loading }
 *
 * Behavior:
 *   - On mount, restores cached state from localStorage so reloads don't flash
 *     "not impersonating" before the network fetch resolves.
 *   - Polls GET /api/admin/v2/impersonation/current every 30s.
 *   - Re-derives minutesRemaining from expires_at every 30s so the banner
 *     counts down without an extra network call.
 */
export function useImpersonation() {
  const [state, setState] = useState(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Drop stale cache if already expired
        if (parsed && parsed.active && parsed.expires_at) {
          const expiresMs = new Date(parsed.expires_at).getTime();
          if (Number.isFinite(expiresMs) && expiresMs > Date.now()) {
            return { ...parsed, loading: false };
          }
        }
      }
    } catch { /* ignore cache parse errors */ }
    return { active: false, loading: true };
  });

  // Tick re-renders the countdown without a network call
  const [, setTick] = useState(0);
  const mountedRef = useRef(true);

  const fetchCurrent = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/v2/impersonation/current`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) {
        if (mountedRef.current) {
          setState({ active: false, loading: false });
          try { localStorage.removeItem(CACHE_KEY); } catch {}
        }
        return;
      }
      const data = await res.json();
      if (!mountedRef.current) return;
      if (data && data.active) {
        const next = {
          active: true,
          target_org: data.target_org,
          expires_at: data.expires_at,
          started_at: data.started_at,
          reason: data.reason,
          mode: data.mode || 'read_only',
          impersonation_id: data.impersonation_id,
          loading: false,
        };
        setState(next);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch {}
      } else {
        setState({ active: false, loading: false });
        try { localStorage.removeItem(CACHE_KEY); } catch {}
      }
    } catch {
      // Network/parse errors leave the cached optimistic state in place so the
      // banner doesn't flicker offline. We just stop the spinner.
      if (mountedRef.current) setState(s => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchCurrent();
    const networkTimer = setInterval(fetchCurrent, POLL_INTERVAL_MS);
    const tickTimer = setInterval(() => setTick(t => t + 1), POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(networkTimer);
      clearInterval(tickTimer);
    };
  }, [fetchCurrent]);

  const endImpersonation = useCallback(async () => {
    try {
      await fetch(`${API}/api/admin/v2/impersonation/end`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch { /* server-side audit log is the source of truth; ignore */ }
    try { localStorage.removeItem(CACHE_KEY); } catch {}
    setState({ active: false, loading: false });
  }, []);

  // Derived countdown (recomputed every render; the tickTimer triggers renders)
  let minutesRemaining = null;
  if (state.active && state.expires_at) {
    const ms = new Date(state.expires_at).getTime() - Date.now();
    minutesRemaining = Math.max(0, Math.ceil(ms / 60000));
  }

  return {
    ...state,
    minutesRemaining,
    endImpersonation,
    refetch: fetchCurrent,
  };
}

export default useImpersonation;
